import {
  Box,
  Button,
  Container,
  Grid,
  IconButton,
  Rating,
  Skeleton,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import StarIcon from "@mui/icons-material/Star";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { useState } from "react";
import { useGoogleReviews, GoogleReview } from "../../hooks/useGoogleReviews";

// Direct link to the reviews tab for Hoppenhagen on Google Maps
const GOOGLE_REVIEWS_URL =
  "https://www.google.com/maps/place/?q=place_id:ChIJTXi7qcFTUkYRxtH90PoEd1c";

/** Shown when the API returns no data or isn't configured yet */
const FALLBACK_REVIEWS: GoogleReview[] = [
  {
    author: "Nikola Salacz",
    rating: 5,
    text: "Pure fun! Great energy! Classes runned by two powerful and sexy women that will make you feel the same way! Everyone should join at least once!",
    time: "",
  },
  {
    author: "Selgin Deniz Cakal",
    rating: 5,
    text: "Very fun fitness classes. You dance and jump in the spring shoes for a good cardio. They can be booked for special events too!",
    time: "",
  },
  {
    author: "Tavi",
    rating: 5,
    text: "Even as a dude I still had so much fun, need to practice more my coordination skills 😂",
    time: "",
  },
];

function chunkArray<T>(arr: T[], size: number): T[][] {
  return Array.from({ length: Math.ceil(arr.length / size) }, (_, i) =>
    arr.slice(i * size, i * size + size)
  );
}

const ReviewCard = ({ review }: { review: GoogleReview }) => (
  <Box
    sx={{
      height: "100%",
      display: "flex",
      flexDirection: "column",
      backgroundColor: "background.default",
      borderRadius: 3,
      p: 3,
      border: (theme) => `1px solid ${alpha(theme.palette.divider, 1)}`,
    }}
  >
    <Rating
      value={review.rating}
      readOnly
      size="small"
      icon={<StarIcon fontSize="inherit" />}
      sx={{ color: "#fbbc04", mb: 1.5 }}
    />
    <Typography
      variant="body2"
      sx={{ color: "text.primary", lineHeight: 1.75, flexGrow: 1, mb: 2 }}
    >
      "{review.text}"
    </Typography>
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
      }}
    >
      <Typography
        variant="caption"
        sx={{ fontWeight: 700, color: "text.secondary" }}
      >
        {review.author}
      </Typography>
      {review.time && (
        <Typography variant="caption" sx={{ color: "text.disabled" }}>
          {review.time}
        </Typography>
      )}
    </Box>
  </Box>
);

export const Testimonials = () => {
  const { reviews: liveReviews, rating, total, loading } = useGoogleReviews();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const reviews =
    !loading && liveReviews.length > 0 ? liveReviews : FALLBACK_REVIEWS;

  const pageSize = isMobile ? 1 : 3;
  const slides = chunkArray(reviews, pageSize);

  const [page, setPage] = useState(0);
  const prev = () => setPage((p) => Math.max(0, p - 1));
  const next = () => setPage((p) => Math.min(slides.length - 1, p + 1));

  return (
    <Box
      id="testimonials"
      sx={{
        width: "100%",
        backgroundColor: "background.paper",
        py: { xs: 8, sm: 12 },
        px: { xs: 3, sm: 6 },
      }}
    >
      <Container maxWidth="lg">
        {/* Section heading */}
        <Box sx={{ textAlign: "center", mb: { xs: 6, sm: 8 } }}>
          {/* Google badge */}
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 0.8,
              mb: 2,
              px: 2,
              py: 0.8,
              borderRadius: 10,
              border: (theme) => `1px solid ${alpha(theme.palette.divider, 1)}`,
            }}
          >
            <Box
              component="img"
              src="https://upload.wikimedia.org/wikipedia/commons/2/2f/Google_2015_logo.svg"
              alt="Google"
              sx={{ height: 18, display: "block" }}
            />
            <Typography
              variant="caption"
              sx={{ fontWeight: 700, color: "text.secondary" }}
            >
              Reviews
            </Typography>
          </Box>

          <Typography
            component="h2"
            variant="h4"
            sx={{ fontWeight: 900, color: "text.primary", mb: 1 }}
          >
            What our members say
          </Typography>

          {/* Aggregate rating - shown when live data is available */}
          {!loading && rating !== null && total !== null && (
            <Box
              sx={{ display: "inline-flex", alignItems: "center", gap: 1, mt: 1 }}
            >
              <Typography variant="h6" sx={{ fontWeight: 900, color: "text.primary" }}>
                {rating.toFixed(1)}
              </Typography>
              <Rating
                value={rating}
                precision={0.1}
                readOnly
                size="small"
                icon={<StarIcon fontSize="inherit" />}
                sx={{ color: "#fbbc04" }}
              />
              <Typography variant="body2" sx={{ color: "text.secondary" }}>
                ({total})
              </Typography>
            </Box>
          )}
        </Box>

        {/* Review cards / skeleton */}
        {loading ? (
          <Grid container spacing={3} justifyContent="center">
            {Array.from({ length: 3 }).map((_, i) => (
              <Grid item xs={12} md={4} key={i}>
                <Box
                  sx={{
                    borderRadius: 3,
                    p: 3,
                    backgroundColor: "background.default",
                    border: (theme) =>
                      `1px solid ${alpha(theme.palette.divider, 1)}`,
                  }}
                >
                  <Skeleton variant="rectangular" width={80} height={16} sx={{ borderRadius: 1, mb: 1.5 }} />
                  <Skeleton variant="text" sx={{ fontSize: "0.9rem" }} />
                  <Skeleton variant="text" sx={{ fontSize: "0.9rem" }} />
                  <Skeleton variant="text" width="60%" sx={{ fontSize: "0.9rem", mb: 2 }} />
                  <Skeleton variant="text" width="40%" />
                </Box>
              </Grid>
            ))}
          </Grid>
        ) : (
          <>
            {/* Carousel slide */}
            <Grid
              container
              spacing={3}
              justifyContent={slides[page].length < pageSize ? "center" : "flex-start"}
            >
              {slides[page].map((review, i) => (
                <Grid item xs={12} md={4} key={i}>
                  <ReviewCard review={review} />
                </Grid>
              ))}
            </Grid>

            {/* Navigation - only shown when there are multiple slides */}
            {slides.length > 1 && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 2,
                  mt: 4,
                }}
              >
                <IconButton
                  onClick={prev}
                  disabled={page === 0}
                  aria-label="Previous reviews"
                  sx={{
                    border: (theme) => `1px solid ${alpha(theme.palette.divider, 1)}`,
                    "&:hover": { backgroundColor: "background.default" },
                  }}
                >
                  <ChevronLeftIcon />
                </IconButton>

                {/* Dot indicators */}
                <Box sx={{ display: "flex", gap: 1 }}>
                  {slides.map((_, i) => (
                    <Box
                      key={i}
                      onClick={() => setPage(i)}
                      sx={{
                        width: i === page ? 20 : 8,
                        height: 8,
                        borderRadius: 4,
                        backgroundColor: i === page ? "primary.main" : "divider",
                        cursor: "pointer",
                        transition: "all 0.25s ease",
                      }}
                    />
                  ))}
                </Box>

                <IconButton
                  onClick={next}
                  disabled={page === slides.length - 1}
                  aria-label="Next reviews"
                  sx={{
                    border: (theme) => `1px solid ${alpha(theme.palette.divider, 1)}`,
                    "&:hover": { backgroundColor: "background.default" },
                  }}
                >
                  <ChevronRightIcon />
                </IconButton>
              </Box>
            )}
          </>
        )}

        {/* CTA to Google */}
        <Box sx={{ textAlign: "center", mt: 5 }}>
          <Button
            href={GOOGLE_REVIEWS_URL}
            target="_blank"
            rel="noopener noreferrer"
            variant="outlined"
            endIcon={<OpenInNewIcon fontSize="small" />}
            sx={{
              borderColor: "divider",
              color: "text.primary",
              fontWeight: 700,
              borderRadius: 10,
              px: 3,
              "&:hover": {
                borderColor: "text.secondary",
                bgcolor: "background.default",
              },
            }}
          >
            See all reviews on Google
          </Button>
        </Box>
      </Container>
    </Box>
  );
};
